import { useEffect, useState } from 'react';
import { useCompanyCountry } from '@/contexts/WorkspaceContext';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { PhoneInput } from '@/components/PhoneInput';
import { Trash2, Users, Folder, Phone as PhoneIcon } from 'lucide-react';
import { contactsService, Contact } from '@/services/contacts.service';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';
import { ManualPhone, AudiencePreview, campaignsService } from '@/services/campaigns.service';

type Tab = 'contacts' | 'groups' | 'manual';

interface Props {
  selectedContactUids: string[];
  selectedGroupUids: string[];
  manualPhones: ManualPhone[];
  onChange: (next: { contactUids: string[]; groupUids: string[]; manualPhones: ManualPhone[] }) => void;
}

export const AudiencePicker: React.FC<Props> = ({ selectedContactUids, selectedGroupUids, manualPhones, onChange }) => {
  const [tab, setTab] = useState<Tab>('contacts');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [contactQ, setContactQ] = useState('');
  const [preview, setPreview] = useState<AudiencePreview | null>(null);

  useEffect(() => { contactGroupsService.list().then(setGroups); }, []);
  useEffect(() => {
    contactsService.list({ q: contactQ || undefined, limit: 100 }).then(r => setContacts(r.items));
  }, [contactQ]);

  // Re-preview total whenever selections change
  useEffect(() => {
    let cancelled = false;
    campaignsService.previewAudience({
      audienceContactUids: selectedContactUids,
      audienceGroupUids: selectedGroupUids,
      audienceManualPhones: manualPhones,
    }).then(p => { if (!cancelled) setPreview(p); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [selectedContactUids, selectedGroupUids, manualPhones]);

  const toggleContact = (uid: string) => {
    onChange({
      contactUids: selectedContactUids.includes(uid)
        ? selectedContactUids.filter(u => u !== uid)
        : [...selectedContactUids, uid],
      groupUids: selectedGroupUids,
      manualPhones,
    });
  };

  const toggleGroup = (uid: string) => {
    onChange({
      contactUids: selectedContactUids,
      groupUids: selectedGroupUids.includes(uid)
        ? selectedGroupUids.filter(u => u !== uid)
        : [...selectedGroupUids, uid],
      manualPhones,
    });
  };

  const addManual = (m: ManualPhone) => {
    if (!m.phone || manualPhones.some(p => p.phone === m.phone)) return;
    onChange({ contactUids: selectedContactUids, groupUids: selectedGroupUids, manualPhones: [...manualPhones, m] });
  };

  const removeManual = (phone: string) => {
    onChange({ contactUids: selectedContactUids, groupUids: selectedGroupUids,
      manualPhones: manualPhones.filter(p => p.phone !== phone) });
  };

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="flex border-b border-border">
        <TabBtn active={tab === 'contacts'} onClick={() => setTab('contacts')} icon={<Users size={14} />}
          label={`Contacts${selectedContactUids.length ? ` (${selectedContactUids.length})` : ''}`} />
        <TabBtn active={tab === 'groups'} onClick={() => setTab('groups')} icon={<Folder size={14} />}
          label={`Groups${selectedGroupUids.length ? ` (${selectedGroupUids.length})` : ''}`} />
        <TabBtn active={tab === 'manual'} onClick={() => setTab('manual')} icon={<PhoneIcon size={14} />}
          label={`Manual${manualPhones.length ? ` (${manualPhones.length})` : ''}`} />
      </div>

      <div className="p-4">
        {tab === 'contacts' && (
          <>
            <FormField label="Search">
              <Input value={contactQ} onChange={(e) => setContactQ(e.target.value)} placeholder="Search by name, phone, email…" />
            </FormField>
            <div className="max-h-80 overflow-y-auto border border-border rounded-md">
              {contacts.map(c => (
                <label key={c.contactUid} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 hover:bg-surface-alt cursor-pointer">
                  <input type="checkbox" checked={selectedContactUids.includes(c.contactUid)} onChange={() => toggleContact(c.contactUid)} />
                  <span className="flex-1 text-sm">{c.firstName} {c.lastName ?? ''}</span>
                  <span className="font-mono text-xs text-text-muted">{c.phone}</span>
                </label>
              ))}
              {contacts.length === 0 && <div className="p-4 text-center text-text-muted text-sm">No contacts found.</div>}
            </div>
          </>
        )}

        {tab === 'groups' && (
          <div className="grid grid-cols-2 gap-2">
            {groups.map(g => (
              <label key={g.groupUid} className="flex items-center gap-3 p-3 border border-border rounded-md hover:bg-surface-alt cursor-pointer">
                <input type="checkbox" checked={selectedGroupUids.includes(g.groupUid)} onChange={() => toggleGroup(g.groupUid)} />
                <span className="w-3 h-3 rounded-full" style={{ background: g.color }} />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{g.name}</div>
                  <div className="text-xs text-text-muted">{g.contactCount} members</div>
                </div>
              </label>
            ))}
            {groups.length === 0 && <div className="col-span-2 p-4 text-center text-text-muted text-sm">No groups.</div>}
          </div>
        )}

        {tab === 'manual' && (
          <ManualPhoneEntry onAdd={addManual} existing={manualPhones} onRemove={removeManual} />
        )}

        <div className="mt-4 p-3 bg-surface-alt rounded-md text-sm">
          <div className="flex justify-between">
            <span className="text-text-muted">Estimated audience</span>
            <strong className="text-text-primary">
              {preview === null ? '…' : `${preview.total} recipients`}
            </strong>
          </div>
          {preview && (
            <div className="text-xs text-text-muted mt-1">
              {preview.contactsCount} from contacts/groups (opted-in only) · {preview.manualCount} manual phones
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TabBtn: React.FC<{ active: boolean; onClick: () => void; label: string; icon: React.ReactNode }> = ({ active, onClick, label, icon }) => (
  <button type="button" onClick={onClick}
    className={`flex-1 px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 ${active ? 'text-primary border-b-2 border-primary' : 'text-text-muted'}`}>
    {icon}{label}
  </button>
);

const ManualPhoneEntry: React.FC<{ onAdd: (m: ManualPhone) => void; existing: ManualPhone[]; onRemove: (phone: string) => void }> = ({ onAdd, existing, onRemove }) => {
  const companyCountry = useCompanyCountry();
  const [phone, setPhone] = useState(''); const [pickedCountry, setCountry] = useState<string | null>(null);
  const country = pickedCountry ?? companyCountry;
  const [valid, setValid] = useState(false);
  return (
    <>
      <FormField label="Add phone number">
        <PhoneInput onChange={(v) => { setPhone(v.e164); setCountry(v.country ?? country); setValid(v.isValid); }} />
        <Button size="sm" className="mt-2" disabled={!valid} onClick={() => { onAdd({ phone, countryCode: country }); }}>+ Add</Button>
      </FormField>
      <div className="max-h-60 overflow-y-auto border border-border rounded-md">
        {existing.map(m => (
          <div key={m.phone} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0">
            <span className="flex-1 font-mono text-xs">{m.phone}</span>
            <span className="text-xs text-text-muted">{m.countryCode}</span>
            <button type="button" onClick={() => onRemove(m.phone)} className="text-text-muted hover:text-danger">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {existing.length === 0 && <div className="p-4 text-center text-text-muted text-sm">No manual phones added.</div>}
      </div>
    </>
  );
};
